import { TAG } from "../constants";
import { BucketFile, BucketProps } from "../types";
import { patientApi } from "./patientApi";

export const bucketApi = patientApi.injectEndpoints({
  endpoints: (builder) => ({
    // bucket
    downloadPrivateFile: builder.query<
      { fileUrl: string; type: string } | null,
      string
    >({
      query: (path) => ({
        url: `/api/patient/id-verification/download/${encodeURIComponent(
          path,
        )}`,
        method: "GET",
      }),

      providesTags(_result, _error, arg) {
        return [{ type: TAG.ID_VERIFICATION, id: arg }];
      },
    }),

    downloadInjectionGuide: builder.query<string, BucketProps>({
      query: (body) => ({
        method: "POST",
        url: "/api/bucket",
        responseHandler: "text",
        body,
      }),
      transformResponse: (res: string) => res,
    }),
    getActiveFiles: builder.query<{ title: string; url: string }[], string>({
      query: (bucket) => ({
        method: "GET",
        url: `/api/patient/files/${bucket}`,
      }),
      providesTags: [TAG.FILE_STORAGE, TAG.USER],
    }),
    getFile: builder.query<
      BucketFile,
      { folderName: string; fileName: string }
    >({
      query: ({ folderName, fileName }) => ({
        url: `/api/patient/bucket/${folderName}/files/${fileName}`,
        method: "GET",
      }),
      providesTags(_result, _error, arg) {
        return [{ type: TAG.FILE_STORAGE, id: arg.fileName }];
      },
    }),
    getFilesFromFolder: builder.query<BucketFile[], string>({
      query: (folderName) => ({
        url: `/api/patient/bucket/${folderName}/files`,
        method: "GET",
      }),
      providesTags(_result, _error, arg) {
        return [{ type: TAG.FILE_STORAGE, id: arg }];
      },
    }),
    uploadFile: builder.mutation<
      BucketFile,
      { file: File | Blob; name: string; type: string; folder: string }
    >({
      query: ({ file, name, type, folder }) => {
        const formData = new FormData();
        formData.append("file", file, name);
        formData.append("type", type);

        return {
          method: "POST",
          url: `/api/patient/bucket/upload/${folder}`,
          body: formData,
        };
      },
      invalidatesTags: (_res, _error, args) => [
        { type: TAG.FILE_STORAGE, id: args.folder },
      ],
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          bucketApi.util.updateQueryData(
            "getFile",
            { folderName: args.folder, fileName: args.name },
            () => data,
          ),
        );
      },
    }),
  }),
});

export const {
  useLazyDownloadInjectionGuideQuery,
  useGetActiveFilesQuery,
  useDownloadPrivateFileQuery,
  useGetFileQuery,
  useUploadFileMutation,
  useGetFilesFromFolderQuery,
} = bucketApi;
